import React, { useMemo, useState, useEffect } from 'react';
import {
	FormOutlined,
	AppstoreOutlined,
	TableOutlined,
	ArrowRightOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Spin, DatePicker, Input, Segmented, Button } from 'antd';

import Header from '@/components/Header/Header';
import { Loading } from '@/components/Loading';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgentId, getLoggedInUser } from '@/store/account/selector';
import { getTickets, updateTicket } from '@/api/tickets';
import { RawTicket } from './types';

import { processRawTicketsIntoTickets } from './ticketProcessor';
import { Ticket, TicketStatus } from './types';
import { TicketView } from './components/TicketView';
import { ResponseDetailsModal } from './components/ResponseDetailsModal';
import { FormResponseTable } from './components/FormResponseTable';
import { FormGroupTabs } from './components/FormGroupTabs';
import { CallDetailsModal } from './components/CallDetailsModal';
import './FormResponsesPage.scss';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { useLocation, useNavigate } from 'react-router-dom';
import { setCurrentPage } from '@/store/account/slice';
import { ReceptionistDashboardSiderItem } from 'src/enums/EReceptionistDashboardSiderItem';

const { RangePicker } = DatePicker;

interface FormGroup {
	id: string;
	title: string;
	questions: string[];
	responses: Ticket[];
}

const TicketsPageContent: React.FC = () => {
	const { notification } = App.useApp();
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const loggedInUser = useAppSelector(getLoggedInUser);
	const queryClient = useQueryClient();
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	const [detailsTicket, setDetailsTicket] = useState<Ticket | null>(null);
	const [dateRange, setDateRange] = useState<[any, any] | null>(null);
	const [viewMode, setViewMode] = useState<'ticket' | 'table'>('ticket');
	const [questionSearchInput, setQuestionSearchInput] = useState('');
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
	const [activeFormGroupId, setActiveFormGroupId] = useState<string | null>(null);
	const [viewingCallDetailsTicket, setViewingCallDetailsTicket] = useState<Ticket | null>(null);

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedSearchTerm(questionSearchInput);
		}, 300);

		return () => {
			clearTimeout(handler);
		};
	}, [questionSearchInput]);

	const startDate = dateRange?.[0] ? dateRange[0].startOf('day').valueOf() : null;
	const endDate = dateRange?.[1] ? dateRange[1].endOf('day').valueOf() : null;

	const queryKey = ['tickets', selectedAgentId, startDate, endDate];

	const { data: responseData, isLoading: isQueryLoading } = useQuery({
		queryKey,
		queryFn: () => getTickets(selectedAgentId as string, { startDate, endDate }),
		enabled: !!selectedAgentId,
	});

	const workflowTitleMap = useMemo(() => responseData?.workflowTitleMap || {}, [responseData]);

	const allTickets: Ticket[] = useMemo(() => {
		if (!responseData?.tickets) {
			return [];
		}
		return processRawTicketsIntoTickets(responseData.tickets as RawTicket[]);
	}, [responseData]);

	const filteredTickets: Ticket[] = useMemo(() => {
		if (!debouncedSearchTerm) {
			return allTickets;
		}
		const lowercasedSearch = debouncedSearchTerm.toLowerCase();
		return allTickets.filter((ticket) =>
			ticket.questions.some((q) => q.toLowerCase().includes(lowercasedSearch)),
		);
	}, [allTickets, debouncedSearchTerm]);

	const isFilterActive = !!(dateRange || debouncedSearchTerm);

	const formGroups: FormGroup[] = useMemo(() => {
		const groups = new Map<string, { title: string; questions: string[]; responses: Ticket[] }>();

		filteredTickets.forEach((ticket) => {
			const groupId = ticket.workflowId || 'default-group';
			const currentTitle = workflowTitleMap[groupId];
			const groupTitle = currentTitle || ticket.ticketTitle || 'Intake Form';

			if (!groups.has(groupId)) {
				groups.set(groupId, { title: groupTitle, questions: ticket.questions, responses: [] });
			}
			groups.get(groupId)!.title = groupTitle;
			groups.get(groupId)!.responses.push(ticket);
		});

		return Array.from(groups.entries()).map(([groupId, groupData]) => ({
			id: groupId,
			title: groupData.title,
			responses: groupData.responses.sort((a, b) => b.callDate - a.callDate),
			questions: groups.get(groupId)?.questions || [],
		}));
	}, [filteredTickets, workflowTitleMap]);

	useEffect(() => {
		setActiveFormGroupId(null);
	}, [startDate, endDate, debouncedSearchTerm]);

	useEffect(() => {
		const activeGroupExists = formGroups.some((g) => g.id === activeFormGroupId);
		if (!activeGroupExists && formGroups.length > 0) {
			setActiveFormGroupId(formGroups[0].id);
		}
	}, [formGroups, activeFormGroupId]);

	const ticketsForActiveTab = useMemo(() => {
		if (!activeFormGroupId) {
			return [];
		}
		const activeGroup = formGroups.find((g) => g.id === activeFormGroupId);
		return activeGroup ? activeGroup.responses : [];
	}, [formGroups, activeFormGroupId]);

	const { mutate, isPending: isMutationPending } = useMutation({
		mutationFn: updateTicket,
		onSuccess: () => {
			notification.success({
				message: 'Ticket Updated',
			});
			queryClient.invalidateQueries({ queryKey });
		},
		onError: () => {
			notification.error({
				message: 'Update Failed',
				description: 'Your change could not be saved.',
			});
		},
	});

	const handleStatusChange = (ticketId: string, status: TicketStatus) => {
		if (!selectedAgentId) {
			return;
		}
		const parts = ticketId.split('_');
		const callSid = parts[0];
		const rawTicketId = parts.slice(1).join('_');
		mutate({ agentId: selectedAgentId, callSid, ticketId: rawTicketId, status });
	};

	const handleNoteSave = (ticketId: string, note: string) => {
		if (!selectedAgentId) {
			return;
		}
		const parts = ticketId.split('_');
		const callSid = parts[0];
		const rawTicketId = parts.slice(1).join('_');
		mutate({ agentId: selectedAgentId, callSid, ticketId: rawTicketId, note });
	};

	const handleViewCallDetails = (ticket: Ticket) => {
		setViewingCallDetailsTicket(ticket);
	};

	const handleNavigateAndConfigure = () => {
		dispatch(setCurrentPage(ReceptionistDashboardSiderItem.RECEPTIONIST));

		const isResellerPortal = location.pathname.includes('/reseller_portal');
		let basePath = '/';
		if (isResellerPortal) {
			basePath = '/reseller_portal';
		} else if (loggedInUser?.resellerClient) {
			basePath = '/dashboard';
		}

		navigate(basePath, {
			state: { openAccordionSection: 'Intake Questions' },
		});
	};

	return (
		<div className="form-responses-page">
			<Spin
				spinning={isMutationPending}
				size="default"
				style={{
					position: 'fixed',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
					zIndex: 9999,
				}}
			/>
			<div className={isMutationPending ? 'content-blur' : ''}>
				<div>
					<Header
						title="Tickets"
						description="View and manage all your Tickets"
						icon={<FormOutlined />}
					/>
					<div className="page-layout-container">
						<div className="filter-bar">
							<RangePicker
								onChange={(dates: any) => setDateRange(dates)}
								value={dateRange}
								disabled={allTickets.length === 0 && !isFilterActive}
								allowEmpty={[true, true]}
							/>
							<Input.Search
								placeholder="Search questions..."
								allowClear
								onChange={(e) => setQuestionSearchInput(e.target.value)}
								style={{ flex: 1, minWidth: '200px', maxWidth: '400px' }}
								disabled={allTickets.length === 0 && !isFilterActive}
							/>
							<Segmented
								value={viewMode}
								onChange={(value) => setViewMode(value as 'ticket' | 'table')}
								options={[
									{ label: 'Card', value: 'ticket', icon: <AppstoreOutlined /> },
									{ label: 'Table', value: 'table', icon: <TableOutlined /> },
								]}
							/>
						</div>

						{isQueryLoading ? (
							<Loading />
						) : filteredTickets.length === 0 ? (
							isFilterActive ? (
								<div className="empty-page-placeholder">
									<FormOutlined />
									<h3>No Tickets Found</h3>
									<p>
										No tickets match your current filter settings. Try adjusting your date range.
									</p>
								</div>
							) : (
								<div className="educational-empty-state">
									<div className="icon-wrapper">
										<FormOutlined />
									</div>
									<h3 className="headline">No Tickets Yet!</h3>
									<p className="body-text">
										Create an intake form to start managing all your booking requests, work orders,
										tickets, and more!
									</p>
									<Button
										type="primary"
										size="large"
										onClick={handleNavigateAndConfigure}
										icon={<ArrowRightOutlined />}
										className="cta-button"
									>
										Create an Intake Form
									</Button>
								</div>
							)
						) : (
							<>
								<FormGroupTabs
									formGroups={formGroups}
									activeTabId={activeFormGroupId || ''}
									onTabChange={setActiveFormGroupId}
								/>
								{viewMode === 'ticket' ? (
									<TicketView
										tickets={ticketsForActiveTab}
										onStatusChange={handleStatusChange}
										onNoteSave={handleNoteSave}
										onViewDetails={setDetailsTicket}
										onViewCallDetails={handleViewCallDetails}
									/>
								) : (
									<FormResponseTable
										formGroups={formGroups.filter((g) => g.id === activeFormGroupId)}
										onStatusChange={handleStatusChange}
										onNoteSave={handleNoteSave}
										onViewDetails={setDetailsTicket}
										onViewCallDetails={handleViewCallDetails}
									/>
								)}
							</>
						)}
					</div>

					<ResponseDetailsModal ticket={detailsTicket} onClose={() => setDetailsTicket(null)} />
					<CallDetailsModal
						ticket={viewingCallDetailsTicket}
						shareableLink={viewingCallDetailsTicket?.shareableLink || null}
						onClose={() => setViewingCallDetailsTicket(null)}
					/>
				</div>
			</div>
		</div>
	);
};

const TicketsPage: React.FC = () => (
	<App>
		<TicketsPageContent />
	</App>
);

export default TicketsPage;
